import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef } from "react";
import PillarTableColumnChild from "shared/components/pillar-table/cell-content/PillarTableColumnChild";
import {
  pillarTableColumnArrayValue,
  type DeepValue,
  type PathToArray,
  type UnwrapArray,
} from "shared/components/pillar-table/columns/array/PillarTableColumnArray";
import { PillarTableColumnArrayJoin } from "shared/components/pillar-table/columns/array/PillarTableColumnArrayJoin";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

type PillarTableColumnArrayFilterProps<T> = {
  dataType?: "string" | "number" | PillarTableSafeAny;
  findPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  outputPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  /**
   * When using an array of strings or numbers for findValue, the column will match any of the values provided.
   */
  findValue: string | number | string[] | number[];
  children?: React.ReactNode;
} & Omit<PillarTableBaseColumnCellValuePathProps<T>, "cellValuePath"> & {
    cellValuePath?: PathToArray<T>;
  } & ComponentPropsWithoutRef<"div">;

export const PillarTableColumnArrayFilter = <T,>({
  cellValuePath,
  children,
  dataType,
  findPath,
  findValue,
  outputPath,
}: PillarTableColumnArrayFilterProps<T>) => {
  const rowContext = usePillarTableRowContext();
  const arrayValue = pillarTableColumnArrayValue({
    rowContext,
    cellValuePath,
  });

  const filteredArray = arrayValue.filter(
    (item: DeepValue<T, PathToArray<T>>) => {
      const valueAtPath =
        typeof item === "object" &&
        item !== null &&
        findPath !== undefined &&
        findPath !== ""
          ? lodashGet(item, findPath as string)
          : item;
      if (Array.isArray(findValue)) {
        return findValue.some((val) => valueAtPath === val);
      }
      return valueAtPath === findValue;
    },
  );
  if (dataType === undefined && children === undefined) {
    children = <PillarTableColumnArrayJoin cellValuePath="" />;
  }
  return (
    <PillarTableColumnChild
      rowValueOverride={filteredArray}
      rowIndex={rowContext?.rowIndex}
      cellValuePath={outputPath}
      columnConfig={dataType}
    >
      {children}
    </PillarTableColumnChild>
  );
};
